/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap as cssMapCompiled } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import InfoIcon from '@atlaskit/icon/core/status-information';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { type StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { ChatErrorBanner } from '@atlassian/conversation-assistant-ui-components/ui/ChatErrorBanner';
import { Paper } from '@atlassian/conversation-assistant-ui-components/ui/Paper';

import type { ConversationAssistantUIModes } from '../../types';
import { ConversationTitleBlockContainer } from '../conversation-title-block/conversation-title-block';

import {
	type ExplicitMessageGroupRenderVersion,
	type LatestMessageRendererProps,
	type OlderMessageRendererProps,
} from './message-group-render-stabilization';
import { MessageGroupsContent, type MessageGroupsContentProps } from './message-groups-content';

type StoreChatItemOfType<TType extends StoreChatItem['type']> = Extract<
	StoreChatItem,
	{ type: TType }
>;

type ChatHistoryMessageRenderContext = {
	index: number;
	isLastAssistant: boolean;
	shouldRenderLiveAssistantAffordances: boolean;
};

export type ChatHistoryMessageRenderers = {
	human: (
		message: StoreChatItemOfType<'HUMAN'>,
		context: ChatHistoryMessageRenderContext,
	) => React.ReactNode;
	assistant: (
		message: StoreChatItemOfType<'ASSISTANT'>,
		context: ChatHistoryMessageRenderContext,
	) => React.ReactNode;
	followUp: (
		message: StoreChatItemOfType<'FOLLOW_UP'>,
		context: ChatHistoryMessageRenderContext,
	) => React.ReactNode;
	guardBlock: (
		message: StoreChatItemOfType<'GUARD_BLOCK'>,
		context: ChatHistoryMessageRenderContext,
	) => React.ReactNode;
	error: (
		message: StoreChatItemOfType<'ERROR'>,
		context: ChatHistoryMessageRenderContext,
	) => React.ReactNode;
};

type RenderChatHistoryMessageOptions = {
	storeChatItem: StoreChatItem;
	context: ChatHistoryMessageRenderContext;
	renderers: ChatHistoryMessageRenderers;
};

const renderChatHistoryMessage = ({
	storeChatItem,
	context,
	renderers,
}: RenderChatHistoryMessageOptions): React.ReactNode => {
	switch (storeChatItem.type) {
		case 'HUMAN':
			return renderers.human(storeChatItem, context);
		case 'ASSISTANT':
			return renderers.assistant(storeChatItem, context);
		case 'FOLLOW_UP':
			return renderers.followUp(storeChatItem, context);
		case 'GUARD_BLOCK':
			return renderers.guardBlock(storeChatItem, context);
		case 'ERROR':
			return renderers.error(storeChatItem, context);
		case 'LIVE_CHAT':
		case 'TWILIO_LIVE_CHAT_AGENT':
		case 'PHOTO_CARD_PREVIEW':
		case 'MEMORY_UPDATE':
		case 'ACTION_PREPARE':
		case 'ACTION_COMPLETE':
		case 'USAGE_HINT':
			return null;
		default: {
			const exhaustiveStoreChatItem: never = storeChatItem;
			return exhaustiveStoreChatItem;
		}
	}
};

const styles = cssMap({
	container: {
		height: '100%',
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		position: 'relative',
	},
	scrollingDiv: {
		width: '100%',
		overflow: 'auto',
		paddingTop: token('space.0'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	containerFlex: {
		flex: 1,
	},
});

const stylesCompiled = cssMapCompiled({
	fsCentered: {
		maxWidth: 'min(800px, 50vw)',
		marginInline: 'auto',
	},
});

type ChatTranscriptMessageGroupsContentProps = Omit<
	MessageGroupsContentProps,
	| 'renderMessage'
	| 'renderLatestMessage'
	| 'renderLatestMessageVersion'
	| 'renderOlderMessage'
	| 'renderOlderMessageVersion'
>;

type ChatTranscriptMessageRenderConfiguration = {
	shouldRenderLiveAssistantAffordances: boolean;
	renderVersion?: ExplicitMessageGroupRenderVersion;
};

type ChatTranscriptMessagePresentation = {
	renderers: ChatHistoryMessageRenderers;
	default: ChatTranscriptMessageRenderConfiguration;
	latest?: ChatTranscriptMessageRenderConfiguration;
	older?: ChatTranscriptMessageRenderConfiguration;
	decorateMessage?: (
		storeChatItem: StoreChatItem,
		index: number,
		renderedMessage: React.ReactNode,
	) => React.ReactNode;
};

export type ChatTranscriptProps = {
	chatHistoryContainerRef: React.RefObject<HTMLDivElement>;
	messageGroupsContentProps: ChatTranscriptMessageGroupsContentProps;
	messagePresentation: ChatTranscriptMessagePresentation;
	fullScreen?: boolean;
	isFullScreenErrorState: boolean;
	isPulseExperimentEnabled: boolean;
	liveRegionText?: string;
	mode?: ConversationAssistantUIModes;
	shouldShowConversationHistoryWarning: boolean;
	conversationMessagesResponseCount: number | null;
	conversationHistoryWarningMessage: React.ReactNode;
	showSkills: React.ReactNode;
	showPromptLibrary: React.ReactNode;
};

export const ChatTranscript = ({
	fullScreen,
	isFullScreenErrorState,
	isPulseExperimentEnabled,
	liveRegionText,
	mode,
	shouldShowConversationHistoryWarning,
	conversationMessagesResponseCount,
	conversationHistoryWarningMessage,
	showSkills,
	showPromptLibrary,
	chatHistoryContainerRef,
	messageGroupsContentProps,
	messagePresentation,
}: ChatTranscriptProps): React.JSX.Element => {
	const renderMessageWithConfiguration = (
		storeChatItem: StoreChatItem,
		index: number,
		isLastAssistant: boolean,
		configuration: ChatTranscriptMessageRenderConfiguration,
	): React.ReactNode => {
		const renderedMessage = renderChatHistoryMessage({
			storeChatItem,
			context: {
				index,
				isLastAssistant,
				shouldRenderLiveAssistantAffordances: configuration.shouldRenderLiveAssistantAffordances,
			},
			renderers: messagePresentation.renderers,
		});

		return messagePresentation.decorateMessage
			? messagePresentation.decorateMessage(storeChatItem, index, renderedMessage)
			: renderedMessage;
	};

	const renderMessage = (storeChatItem: StoreChatItem, index: number, isLastAssistant: boolean) =>
		renderMessageWithConfiguration(
			storeChatItem,
			index,
			isLastAssistant,
			messagePresentation.default,
		);
	const latestMessageConfiguration = messagePresentation.latest;
	const olderMessageConfiguration = messagePresentation.older;
	const renderLatestMessage = latestMessageConfiguration
		? (storeChatItem: StoreChatItem, index: number, isLastAssistant: boolean) =>
				renderMessageWithConfiguration(
					storeChatItem,
					index,
					isLastAssistant,
					latestMessageConfiguration,
				)
		: undefined;
	const renderOlderMessage = olderMessageConfiguration
		? (storeChatItem: StoreChatItem, index: number) =>
				renderMessageWithConfiguration(storeChatItem, index, false, olderMessageConfiguration)
		: undefined;
	const latestMessageRendererProps: LatestMessageRendererProps = renderLatestMessage
		? {
				renderLatestMessage,
				renderLatestMessageVersion:
					latestMessageConfiguration?.renderVersion ?? renderLatestMessage,
			}
		: {};
	const olderMessageRendererProps: OlderMessageRendererProps = renderOlderMessage
		? {
				renderOlderMessage,
				renderOlderMessageVersion: olderMessageConfiguration?.renderVersion ?? renderOlderMessage,
			}
		: {};
	const resolvedMessageGroupsContentProps: MessageGroupsContentProps = {
		...messageGroupsContentProps,
		renderMessage,
		...latestMessageRendererProps,
		...olderMessageRendererProps,
	};

	return (
		<Box xcss={cx(styles.container, fg('rovo_skills_directory_ui') && styles.containerFlex)}>
			{fg('rovo_chat_trace_aria_live_updates') && (
				<VisuallyHidden testId="chat-history-status-live-region" role="status">
					{liveRegionText}
				</VisuallyHidden>
			)}
			{isPulseExperimentEnabled ? <ConversationTitleBlockContainer /> : null}
			<Box ref={chatHistoryContainerRef} xcss={styles.scrollingDiv} testId="chat-history-container">
				{isFullScreenErrorState && <ChatErrorBanner />}
				<div css={[fullScreen && stylesCompiled.fsCentered]}>
					{shouldShowConversationHistoryWarning && (
						<Paper variant="neutral" mode={mode?.toString().toLowerCase()}>
							<Inline alignBlock="center" space="space.100">
								<InfoIcon label="" color={token('color.icon.information')} size="small" />
								<Text weight="semibold" size="medium">
									{conversationHistoryWarningMessage}
								</Text>
							</Inline>
						</Paper>
					)}
					<MessageGroupsContent {...resolvedMessageGroupsContentProps} />
				</div>
			</Box>

			{showSkills}
			{showPromptLibrary}
			<UFOCustomData
				data={{
					conversationHistorySubScreen: 'messages-screen',
					conversationMessagesResponseCount,
					messageGroupsCount: messageGroupsContentProps.messageGroups.length,
					olderMessageGroupsCount: messageGroupsContentProps.olderMessageGroups.length,
				}}
			/>
		</Box>
	);
};
